<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <div class="section-panel">
            <div class="section-panel-header">
              <v-icon size="22" color="#ffffff">mdi-help-circle-outline</v-icon>
              <span>PREGUNTAS FRECUENTES</span>
            </div>

            <div class="faq-body">
              <div v-if="loading" class="faq-state">
                <v-progress-circular indeterminate color="#c8102e" size="34" />
              </div>

              <p v-else-if="error" class="faq-state faq-state-text">
                No pudimos cargar las preguntas frecuentes. Intenta nuevamente más tarde.
              </p>

              <p v-else-if="!faqs.length" class="faq-state faq-state-text">
                Aún no hay preguntas frecuentes publicadas.
              </p>

              <template v-else>
                <section
                  v-for="group in groups"
                  :key="group.name || 'general'"
                  class="faq-group"
                >
                  <h3 v-if="group.name" class="faq-group-title">{{ group.name }}</h3>

                  <div
                    v-for="faq in group.items"
                    :key="faq.id"
                    class="faq-item"
                    :class="{ 'faq-item-open': isOpen(faq.id) }"
                  >
                    <button
                      type="button"
                      class="faq-question"
                      :aria-expanded="isOpen(faq.id) ? 'true' : 'false'"
                      :aria-controls="`faq-answer-${faq.id}`"
                      @click="toggle(faq.id)"
                    >
                      <span class="faq-question-text">{{ faq.question }}</span>
                      <v-icon class="faq-chevron" size="22" color="#c8102e">
                        mdi-chevron-down
                      </v-icon>
                    </button>

                    <v-expand-transition>
                      <div v-show="isOpen(faq.id)">
                        <p :id="`faq-answer-${faq.id}`" class="faq-answer">{{ faq.answer }}</p>
                      </div>
                    </v-expand-transition>
                  </div>
                </section>
              </template>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchFaqs } from "@/services/teatroApi";

export default {
  name: "TeatroFaq",
  data() {
    return {
      faqs: [],
      openIds: [],
      loading: true,
      error: false,
    };
  },
  computed: {
    // Agrupa por categoría respetando el orden que entrega el backend.
    groups() {
      const groups = [];
      const byName = {};
      for (const faq of this.faqs) {
        const name = (faq.category || "").trim();
        if (!byName[name]) {
          byName[name] = { name, items: [] };
          groups.push(byName[name]);
        }
        byName[name].items.push(faq);
      }
      return groups;
    },
  },
  async created() {
    try {
      this.faqs = await fetchFaqs();
    } catch (e) {
      console.error("No se pudieron cargar las preguntas frecuentes:", e);
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    isOpen(id) {
      return this.openIds.includes(id);
    },
    toggle(id) {
      // Varias respuestas pueden quedar abiertas a la vez.
      if (this.isOpen(id)) {
        this.openIds = this.openIds.filter((openId) => openId !== id);
      } else {
        this.openIds = [...this.openIds, id];
      }
    },
  },
};
</script>

<style scoped>
.section-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.section-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.faq-body {
  padding: 26px 34px 34px;
}

.faq-state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.faq-state-text {
  margin: 0;
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 14px;
  letter-spacing: 0.5px;
  text-align: center;
}

.faq-group + .faq-group {
  margin-top: 30px;
}

.faq-group-title {
  margin: 0 0 12px;
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
}

.faq-item {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  overflow: hidden;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.faq-item + .faq-item {
  margin-top: 12px;
}

.faq-item:hover {
  border-color: rgba(255, 255, 255, 0.22);
}

.faq-item-open {
  border-color: rgba(200, 16, 46, 0.55);
  background: rgba(255, 255, 255, 0.06);
}

.faq-question {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.faq-question-text {
  flex: 1;
}

.faq-chevron {
  flex: 0 0 auto;
  transition: transform 0.25s ease;
}

.faq-item-open .faq-chevron {
  transform: rotate(180deg);
}

.faq-answer {
  margin: 0;
  padding: 0 22px 20px;
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14px;
  line-height: 1.65;
  /* Respeta los saltos de línea del texto plano cargado en el admin. */
  white-space: pre-line;
}

@media (max-width: 600px) {
  .faq-body {
    padding: 20px 18px 26px;
  }

  .section-panel-header {
    padding: 0 20px;
    font-size: 13px;
  }

  .faq-question {
    padding: 16px 16px;
    font-size: 13px;
  }

  .faq-answer {
    padding: 0 16px 18px;
    font-size: 13px;
  }
}
</style>
